import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, makeStyles } from '@material-ui/core';
import { Autocomplete, AutocompleteChangeReason, AutocompleteProps, createFilterOptions } from '@material-ui/lab';
import { AxiosRequestConfig } from 'axios';
import { KeyOption, matchSorter } from 'match-sorter';
import useDebounce from 'hooks/useDebounce';

interface AsyncSearchProps<TData> {
  fetch: (config?: AxiosRequestConfig) => Promise<TData[]>;
  config?: AxiosRequestConfig;
  uniqueBy?: keyof TData;
  matchKeys?: readonly KeyOption<TData>[];
  /**
   * Поиск выполняет бэкенд: введённая строка уходит запросом, ответ выводится как есть.
   * Включать только там, где эндпоинт принимает параметр поиска, а fetch пробрасывает config, —
   * иначе поле молча перестанет искать. По умолчанию список грузится целиком и фильтруется
   * на клиенте, поэтому `remoteSearch={false}` писать не нужно: это поведение по умолчанию.
   */
  remoteSearch?: boolean;
  searchParamName?: string;
  /**
   * Кнопка «Выбрать все» в шапке выпадающего списка — только для multiple. Добавляет к выбранному
   * всё, что сейчас видно в списке: при введённом поиске — только найденное. Снять выбор целиком
   * можно крестиком поля, поэтому «Убрать все» нет.
   */
  selectAll?: boolean;
}

interface ISelectAllContext {
  label: string;
  onSelectAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

// Шапка списка живёт в PaperComponent, который Autocomplete рендерит в портале. Данные для неё
// идут контекстом: пересоздавать сам компонент на каждый ввод нельзя — список перемонтировался бы
// и терял прокрутку.
const SelectAllContext = React.createContext<ISelectAllContext | null>(null);

const useStyles = makeStyles((theme) => ({
  selectAll: {
    padding: theme.spacing(0.5, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const SelectAllPaper: React.FC<React.HTMLAttributes<HTMLElement>> = ({ children, ...paperProps }) => {
  const classes = useStyles();
  const selectAll = useContext(SelectAllContext);

  return (
    <Paper {...paperProps}>
      {selectAll && (
        <Box className={classes.selectAll}>
          {/* preventDefault на mousedown: иначе поле теряет фокус и список закрывается до клика. */}
          <Button
            size="small"
            color="primary"
            onMouseDown={(event) => event.preventDefault()}
            onClick={selectAll.onSelectAll}
          >
            {selectAll.label}
          </Button>
        </Box>
      )}
      {children}
    </Paper>
  );
};

function AsyncSearch<
  TData,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  value,
  fetch,
  config,
  uniqueBy,
  matchKeys = [],
  remoteSearch = false,
  searchParamName = 'search',
  selectAll = false,
  ...props
}: AsyncSearchProps<TData> & Omit<AutocompleteProps<TData, Multiple, DisableClearable, FreeSolo>, 'options'>) {
  const [query, setQuery] = useState('');
  const [data, setData] = useState<Map<string, TData[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const defaultFilterOptions = useMemo(() => createFilterOptions<TData>(), []);
  const debouncedValue = useDebounce<string>(query, 1000);
  const mapKey = [debouncedValue, JSON.stringify(config?.params)].filter(Boolean).join('-');

  const handleSearch = useCallback(
    (value: string) => {
      if (!remoteSearch) return;
      setQuery(value);
    },
    [remoteSearch],
  );

  // При серверном поиске ответ выводим как есть: бэкенд уже отфильтровал, и повторный отбор
  // на клиенте отрезал бы найденное по полям, которых нет в matchKeys. Когда поиска на бэкенде
  // нет, список приходит целиком — фильтруем сами: по matchKeys либо стандартным фильтром.
  const getFilterProps = () => {
    if (remoteSearch) return { filterOptions: (options: TData[]) => options };
    if (!matchKeys.length) return {};

    return {
      filterOptions: (options: TData[], { inputValue }: { inputValue: string }) =>
        matchSorter(options, inputValue, { keys: matchKeys }),
    };
  };

  const options = data.get(mapKey) || [];

  // Что добавит «Выбрать все»: видимые сейчас варианты (тем же фильтром, что и у списка), которых
  // ещё нет среди выбранных. Нечего добавлять — кнопки нет.
  const getSelectAllContext = (): ISelectAllContext | null => {
    if (!selectAll || !props.onChange) return null;

    const selected = Array.isArray(value) ? (value as TData[]) : [];
    const isSame = (first: TData, second: TData) =>
      uniqueBy ? first[uniqueBy] === second[uniqueBy] : first === second;
    const filterOptions = getFilterProps().filterOptions ?? defaultFilterOptions;
    const visible = filterOptions(options, {
      inputValue,
      getOptionLabel: props.getOptionLabel ?? String,
    });
    const toAdd = visible.filter((option) => !selected.some((item) => isSame(item, option)));

    if (!toAdd.length) return null;

    const onChange = props.onChange as unknown as (
      event: React.ChangeEvent<{}>,
      next: TData[],
      reason: AutocompleteChangeReason,
    ) => void;

    return {
      label: inputValue ? `Выбрать найденные (${toAdd.length})` : `Выбрать все (${toAdd.length})`,
      onSelectAll: (event) => onChange(event, selected.concat(toAdd), 'select-option'),
    };
  };

  useEffect(() => {
    if (!open) return;
    if (data.has(mapKey)) return;

    setLoading(true);
    fetch({ ...config, params: { ...config?.params, [searchParamName]: debouncedValue } })
      .then((response) => setData(data.set(mapKey, response)))
      .finally(() => setLoading(false));
  }, [open, debouncedValue, config?.params]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SelectAllContext.Provider value={getSelectAllContext()}>
      <Autocomplete
        open={open}
        value={value}
        options={options}
        loading={loading}
        loadingText="Загрузка..."
        noOptionsText="Пусто"
        {...getFilterProps()}
        getOptionSelected={(option: TData, value: TData) =>
          uniqueBy ? option[uniqueBy] === value[uniqueBy] : option === value
        }
        onInputChange={(_, value, reason) => {
          if (selectAll) setInputValue(value);
          if (reason === 'input') handleSearch(value);
        }}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setQuery('');
          setInputValue('');
        }}
        PaperComponent={selectAll ? SelectAllPaper : undefined}
        {...props}
      />
    </SelectAllContext.Provider>
  );
}

export default AsyncSearch;
